import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Phone, LogIn, KeyRound, MessageCircle, ChevronLeft, HeartHandshake } from "lucide-react";
import { setAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Something went wrong. Please try again.";
}

function PinBoxes({ pin, setPin, refs, testPrefix }) {
  const handleChange = (idx, val) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const next = [...pin];
    next[idx] = digit;
    setPin(next);
    if (digit && idx < 3) refs[idx + 1].current?.focus();
  };
  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !pin[idx] && idx > 0) refs[idx - 1].current?.focus();
  };
  return (
    <div className="flex gap-3 justify-center">
      {pin.map((d, i) => (
        <input
          key={i}
          ref={refs[i]}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          className="w-14 h-16 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2D2766]/20 focus:border-[#2D2766]"
          data-testid={`${testPrefix}-${i}`}
        />
      ))}
    </div>
  );
}

export default function LoginModal({ sessionId, onClose, onSuccess }) {
  const [mode, setMode] = useState("login"); // login | forgot-request | forgot-reset | caregiver-request
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState(["", "", "", ""]);
  const [code, setCode] = useState("");
  const [newPin, setNewPin] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cgName, setCgName] = useState("");
  const [seniorPhone, setSeniorPhone] = useState("");
  const [requested, setRequested] = useState(false);
  const pinRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const newPinRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const validPhone = /^\d{10}$/.test(phone);

  const handleCaregiverRequest = async (e) => {
    e.preventDefault();
    if (cgName.trim().length < 2) { setError("Please enter your name."); return; }
    if (!validPhone) { setError("Please enter your 10-digit WhatsApp number."); return; }
    if (!/^\d{10}$/.test(seniorPhone)) { setError("Please enter your parent's 10-digit number."); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/caregiver/request-access`, { senior_phone: seniorPhone, name: cgName, phone });
      setRequested(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const afterAuthSuccess = async (data) => {
    setAuth(data.profile);
    if (sessionId) {
      try {
        await axios.post(`${API}/user/link-session`, { session_id: sessionId });
      } catch (_) { /* non-fatal */ }
    }
    toast.success(`Welcome back, ${data.profile.name?.split(" ")[0] || "friend"}!`);
    onSuccess?.(data.profile);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const pinStr = pin.join("");
    if (!validPhone) { setError("Please enter your 10-digit phone number."); return; }
    if (!/^\d{4}$/.test(pinStr)) { setError("Please enter your 4-digit PIN."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/user/login`, { phone, pin: pinStr });
      await afterAuthSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    if (!validPhone) { setError("Please enter your 10-digit phone number."); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/user/forgot-pin/request`, { phone });
      toast.success("A reset code has been sent to your WhatsApp number.");
      setMode("forgot-reset");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotReset = async (e) => {
    e.preventDefault();
    const newPinStr = newPin.join("");
    if (!/^\d{6}$/.test(code)) { setError("Please enter the 6-digit code sent on WhatsApp."); return; }
    if (!/^\d{4}$/.test(newPinStr)) { setError("Please set a new 4-digit PIN."); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/user/forgot-pin/reset`, { phone, code, new_pin: newPinStr });
      await afterAuthSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => { setMode("login"); setError(""); setCode(""); setNewPin(["", "", "", ""]); };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm"
      data-testid="login-modal"
    >
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative bg-white rounded-t-[28px] md:rounded-[24px] w-full md:max-w-md p-6 md:p-8 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close"
          data-testid="login-close-btn"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {mode !== "login" && (
          <button
            onClick={backToLogin}
            className="flex items-center gap-1 text-sm font-medium text-[#2D2766] mb-3"
            data-testid="login-back-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Back to login
          </button>
        )}

        {mode === "login" && (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#2D2766]/10 flex items-center justify-center">
                <LogIn className="w-7 h-7 text-[#2D2766]" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1A1A1A]" data-testid="login-title">Log in to Elderra</h2>
              <p className="text-base text-gray-500 mt-1">Use your phone number and the 4-digit PIN you set on WhatsApp.</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="flex items-center gap-1.5 text-base font-semibold text-[#1A1A1A] mb-2">
                  <Phone className="w-4 h-4" /> Phone number
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value.replace(/\D/g, "").slice(0, 10)); setError(""); }}
                  placeholder="10-digit mobile number"
                  className="w-full min-h-[56px] px-4 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2D2766]/20 focus:border-[#2D2766]"
                  data-testid="login-phone-input"
                  autoFocus
                />
              </div>

              <div>
                <label className="text-base font-semibold text-[#1A1A1A] mb-2 block">4-digit PIN</label>
                <PinBoxes pin={pin} setPin={setPin} refs={pinRefs} testPrefix="login-pin" />
              </div>

              {error && <p className="text-center text-sm font-medium text-rose-600" data-testid="login-error">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full min-h-[60px] bg-[#2D2766] text-white rounded-2xl font-semibold text-lg hover:bg-[#3B3382] active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="login-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}
                Log in
              </button>

              <button
                type="button"
                onClick={() => { setMode("forgot-request"); setError(""); }}
                className="w-full text-center text-sm font-semibold text-[#2D2766] hover:underline"
                data-testid="forgot-pin-link"
              >
                Forgot PIN?
              </button>

              <button
                type="button"
                onClick={() => { setMode("caregiver-request"); setError(""); }}
                className="w-full min-h-[52px] rounded-2xl border-2 border-[#2D2766] text-[#2D2766] font-semibold text-base flex items-center justify-center gap-2 hover:bg-[#2D2766]/5"
                data-testid="caregiver-request-link"
              >
                <HeartHandshake className="w-5 h-5" /> I'm a family caregiver
              </button>

              <p className="text-center text-sm text-gray-400">
                No profile yet? Message us on WhatsApp — we'll set it up in one quick chat.
              </p>
            </form>
          </>
        )}

        {mode === "caregiver-request" && (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#2D2766]/10 flex items-center justify-center">
                <HeartHandshake className="w-7 h-7 text-[#2D2766]" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1A1A1A]" data-testid="caregiver-request-title">Ask for caregiver access</h2>
              <p className="text-base text-gray-500 mt-1">
                We'll message your parent on WhatsApp. Once they tap Approve, you'll get a login PIN
                and can see their orders and order for them.
              </p>
            </div>
            {requested ? (
              <div className="space-y-4 text-center" data-testid="caregiver-request-sent">
                <p className="text-base text-[#1A1A1A]">
                  Request sent to <span className="font-semibold">{seniorPhone}</span>. We've asked them to approve it
                  on WhatsApp — you'll get your PIN there as soon as they do.
                </p>
                <button type="button" onClick={() => { setRequested(false); backToLogin(); }}
                  className="w-full min-h-[56px] bg-[#2D2766] text-white rounded-2xl font-semibold text-lg"
                  data-testid="caregiver-request-done-btn">
                  Back to login
                </button>
              </div>
            ) : (
              <form onSubmit={handleCaregiverRequest} className="space-y-5">
                <div>
                  <label className="text-base font-semibold text-[#1A1A1A] mb-2 block">Your name</label>
                  <input
                    value={cgName}
                    onChange={(e) => { setCgName(e.target.value); setError(""); }}
                    placeholder="e.g. Priya Sharma"
                    className="w-full min-h-[56px] px-4 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2D2766]/20 focus:border-[#2D2766]"
                    data-testid="caregiver-name-input"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-1.5 text-base font-semibold text-[#1A1A1A] mb-2">
                    <Phone className="w-4 h-4" /> Your WhatsApp number
                  </label>
                  <input
                    type="tel" inputMode="numeric" value={phone}
                    onChange={(e) => { setPhone(e.target.value.replace(/\D/g, "").slice(0, 10)); setError(""); }}
                    placeholder="10-digit mobile number"
                    className="w-full min-h-[56px] px-4 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2D2766]/20 focus:border-[#2D2766]"
                    data-testid="caregiver-own-phone-input"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-1.5 text-base font-semibold text-[#1A1A1A] mb-2">
                    <Phone className="w-4 h-4" /> Your parent's Elderra number
                  </label>
                  <input
                    type="tel" inputMode="numeric" value={seniorPhone}
                    onChange={(e) => { setSeniorPhone(e.target.value.replace(/\D/g, "").slice(0, 10)); setError(""); }}
                    placeholder="10-digit mobile number"
                    className="w-full min-h-[56px] px-4 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2D2766]/20 focus:border-[#2D2766]"
                    data-testid="caregiver-senior-phone-input"
                  />
                </div>
                {error && <p className="text-center text-sm font-medium text-rose-600" data-testid="caregiver-request-error">{error}</p>}
                <button
                  type="submit" disabled={loading}
                  className="w-full min-h-[60px] bg-[#2D2766] text-white rounded-2xl font-semibold text-lg hover:bg-[#3B3382] active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="caregiver-request-submit-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageCircle className="w-5 h-5" />}
                  Ask for access on WhatsApp
                </button>
              </form>
            )}
          </>
        )}

        {mode === "forgot-request" && (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#2D2766]/10 flex items-center justify-center">
                <MessageCircle className="w-7 h-7 text-[#2D2766]" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1A1A1A]" data-testid="forgot-request-title">Forgot your PIN?</h2>
              <p className="text-base text-gray-500 mt-1">
                Enter your phone number — we'll send a 6-digit code to your WhatsApp. You can also just type
                "forgot pin" to your Elderra WhatsApp chat to reset it there instead.
              </p>
            </div>
            <form onSubmit={handleForgotRequest} className="space-y-5">
              <div>
                <label className="flex items-center gap-1.5 text-base font-semibold text-[#1A1A1A] mb-2">
                  <Phone className="w-4 h-4" /> Phone number
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value.replace(/\D/g, "").slice(0, 10)); setError(""); }}
                  placeholder="10-digit mobile number"
                  className="w-full min-h-[56px] px-4 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2D2766]/20 focus:border-[#2D2766]"
                  data-testid="forgot-phone-input"
                  autoFocus
                />
              </div>
              {error && <p className="text-center text-sm font-medium text-rose-600" data-testid="forgot-request-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full min-h-[60px] bg-[#2D2766] text-white rounded-2xl font-semibold text-lg hover:bg-[#3B3382] active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="forgot-request-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageCircle className="w-5 h-5" />}
                Send code via WhatsApp
              </button>
            </form>
          </>
        )}

        {mode === "forgot-reset" && (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#2D2766]/10 flex items-center justify-center">
                <KeyRound className="w-7 h-7 text-[#2D2766]" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1A1A1A]" data-testid="forgot-reset-title">Enter the code &amp; new PIN</h2>
              <p className="text-base text-gray-500 mt-1">Check WhatsApp for a 6-digit code sent to {phone}.</p>
            </div>
            <form onSubmit={handleForgotReset} className="space-y-5">
              <div>
                <label className="text-base font-semibold text-[#1A1A1A] mb-2 block">6-digit code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
                  placeholder="123456"
                  className="w-full min-h-[56px] px-4 text-lg text-center tracking-widest border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2D2766]/20 focus:border-[#2D2766]"
                  data-testid="forgot-code-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-base font-semibold text-[#1A1A1A] mb-2 block">New 4-digit PIN</label>
                <PinBoxes pin={newPin} setPin={setNewPin} refs={newPinRefs} testPrefix="forgot-new-pin" />
              </div>
              {error && <p className="text-center text-sm font-medium text-rose-600" data-testid="forgot-reset-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full min-h-[60px] bg-[#2D2766] text-white rounded-2xl font-semibold text-lg hover:bg-[#3B3382] active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="forgot-reset-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />}
                Set new PIN &amp; log in
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
